import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MoreHorizontal } from 'lucide-react';
import { GuildRoleBadge } from './GuildRoleBadge';
import type { GuildMemberResponse, GuildMemberRole } from '@/lib/api';

interface GuildMemberListProps {
  members: GuildMemberResponse[];
  myRole: GuildMemberRole | null | undefined;
  onUpdateRole?: (userId: string, role: string) => void;
  onRemove?: (userId: string) => void | Promise<void>;
  onBan?: (userId: string, reason?: string) => void | Promise<void>;
}

const ROLE_HIERARCHY: Record<string, number> = { owner: 4, admin: 3, moderator: 2, member: 1 };

export function GuildMemberList({
  members,
  myRole,
  onUpdateRole,
  onRemove,
  onBan,
}: GuildMemberListProps) {
  const myLevel = myRole ? ROLE_HIERARCHY[myRole] || 0 : 0;
  const [banTarget, setBanTarget] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [kickTarget, setKickTarget] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleBanConfirm = async () => {
    if (banTarget) {
      try {
        setActionError(null);
        await onBan?.(banTarget, banReason.trim() || undefined);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to ban member');
      }
      setBanTarget(null);
      setBanReason('');
    }
  };

  const handleBanCancel = () => {
    setBanTarget(null);
    setBanReason('');
  };

  const handleKickConfirm = async () => {
    if (kickTarget) {
      try {
        setActionError(null);
        await onRemove?.(kickTarget);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to kick member');
      }
      setKickTarget(null);
    }
  };

  const handleKickCancel = () => {
    setKickTarget(null);
  };

  return (
    <>
      <Dialog
        open={banTarget !== null}
        onOpenChange={(open) => {
          if (!open) handleBanCancel();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban Member</DialogTitle>
            <DialogDescription>
              This will ban the user from the guild and remove them from the member list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ban-reason">Reason (optional)</Label>
            <Textarea
              id="ban-reason"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Why is this member being banned?"
              maxLength={500}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleBanCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBanConfirm}>
              Ban
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={kickTarget !== null}
        onOpenChange={(open) => {
          if (!open) handleKickCancel();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kick Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this member from the guild? They can rejoin if the
              guild is public or they have an invite.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleKickCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleKickConfirm}>
              Kick
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {actionError && <p className="text-sm text-destructive mb-2">{actionError}</p>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Joined</TableHead>
            {myLevel >= 2 && <TableHead className="w-[50px]" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => {
            const memberLevel = ROLE_HIERARCHY[member.role] || 0;
            const canManage = myLevel > memberLevel && member.role !== 'owner';

            return (
              <TableRow key={member.userId}>
                <TableCell className="font-mono text-sm">
                  {member.username || member.userId}
                </TableCell>
                <TableCell>
                  <GuildRoleBadge role={member.role} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(member.joinedAt).toLocaleDateString()}
                </TableCell>
                {myLevel >= 2 && (
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Actions for ${member.username || member.userId}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {myLevel >= 3 && memberLevel < myLevel && (
                            <>
                              {member.role !== 'admin' && myLevel >= 4 && (
                                <DropdownMenuItem
                                  onClick={() => onUpdateRole?.(member.userId, 'admin')}
                                >
                                  Promote to Admin
                                </DropdownMenuItem>
                              )}
                              {member.role !== 'moderator' && (
                                <DropdownMenuItem
                                  onClick={() => onUpdateRole?.(member.userId, 'moderator')}
                                >
                                  {memberLevel > 2 ? 'Demote to Moderator' : 'Promote to Moderator'}
                                </DropdownMenuItem>
                              )}
                              {member.role !== 'member' && (
                                <DropdownMenuItem
                                  onClick={() => onUpdateRole?.(member.userId, 'member')}
                                >
                                  Demote to Member
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => setKickTarget(member.userId)}
                            className="text-destructive"
                          >
                            Kick
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setBanTarget(member.userId)}
                            className="text-destructive"
                          >
                            Ban
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}
